import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2, CalendarRange, Clock, UserPlus, PartyPopper, SkipForward, Circle,
} from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { preferencesApi, calendarConnectApi, authApi } from '../api/services';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// OnboardingWizardPage — five-step setup wizard.
//   1. Welcome
//   2. Connect calendar (Microsoft / Google + skip)
//   3. Working hours + lunch
//   4. Invite an EA (or skip)
//   5. Done — completion checklist + Go to Dashboard
//
// Vertical stepper rebuilt from MUI Stepper into a plain numbered list with
// a connector line down the left edge. Completed steps light emerald, the
// current step lights primary, future steps stay muted. The active step's
// body content is revealed inline beneath its label.
// ---------------------------------------------------------------------------

// Microsoft icon SVG inline
function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" fill="none" aria-hidden>
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

// Google icon SVG inline
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

export default function OnboardingWizardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Restore wizard state after OAuth redirect (user navigated away and came back)
  const savedStep = sessionStorage.getItem('onboarding-step');
  const urlParams = new URLSearchParams(window.location.search);
  const cameBackFromOAuth = urlParams.get('connected') === 'microsoft' || urlParams.get('connected') === 'google';

  const [activeStep, setActiveStep] = useState(cameBackFromOAuth ? 1 : (savedStep ? parseInt(savedStep) : 0));

  // Calendar step state. `setCalendarConnected` exists as a stub for future
  // wiring (a polling check could flip this once the OAuth callback writes
  // the new source to /me); right now the value is seeded from the
  // ?connected= query param and read by the checklist.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [calendarConnected, setCalendarConnected] = useState(cameBackFromOAuth);
  const [calendarSkipped, setCalendarSkipped] = useState(false);
  const [isPersonal, setIsPersonal] = useState(false);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [calendarError, setCalendarError] = useState<string | null>(null);

  // Working hours step state
  const [workDayStart, setWorkDayStart] = useState('08:00');
  const [workDayEnd, setWorkDayEnd] = useState('18:00');
  const [lunchStartMinute, setLunchStartMinute] = useState(720);
  const [lunchDurationMinutes, setLunchDurationMinutes] = useState(60);
  const [hoursSaved, setHoursSaved] = useState(false);
  const [hoursSaving, setHoursSaving] = useState(false);
  const [hoursError, setHoursError] = useState<string | null>(null);

  // EA invite step state
  const [eaName, setEaName] = useState('');
  const [eaEmail, setEaEmail] = useState('');
  const [eaInvited, setEaInvited] = useState(false);
  const [eaSkipped, setEaSkipped] = useState(false);
  const [eaInviting, setEaInviting] = useState(false);
  const [eaError, setEaError] = useState<string | null>(null);

  const displayName = user?.displayName?.split(' ')[0] || 'there';

  // Auto-advance to working hours after a successful OAuth return.
  // Strip the ?connected= param so a refresh doesn't re-trigger this.
  useEffect(() => {
    if (cameBackFromOAuth) {
      window.history.replaceState({}, '', window.location.pathname);
      const timer = setTimeout(() => setActiveStep(2), 500);
      return () => clearTimeout(timer);
    }
  }, [cameBackFromOAuth]);

  const handleConnectCalendar = async (provider: 'microsoft' | 'google') => {
    setConnecting(provider);
    setCalendarError(null);
    try {
      const name = provider === 'microsoft' ? 'Microsoft 365' : 'Google Calendar';
      const data = provider === 'microsoft'
        ? await calendarConnectApi.connectMicrosoft({
            name,
            isPersonal,
            eaVisibility: isPersonal ? 'TimeOnly' : undefined,
          })
        : await calendarConnectApi.connectGoogle({
            name,
            isPersonal,
            eaVisibility: isPersonal ? 'TimeOnly' : undefined,
          });
      if (data.authUrl) {
        // Save wizard state before navigating away to OAuth
        sessionStorage.setItem('onboarding-step', '1');
        window.location.href = data.authUrl;
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to start connection';
      setCalendarError(typeof msg === 'string' ? msg : 'Failed to start connection');
      setConnecting(null);
    }
  };

  const handleSaveHours = async () => {
    setHoursSaving(true);
    setHoursError(null);
    try {
      const toTimeOnly = (v: string) => v.length === 5 ? `${v}:00` : v;
      await preferencesApi.update({
        workDayStart: toTimeOnly(workDayStart),
        workDayEnd: toTimeOnly(workDayEnd),
        lunchStartMinute,
        lunchDurationMinutes,
      });
      setHoursSaved(true);
      setTimeout(() => setActiveStep(3), 500);
    } catch (err: any) {
      const data = err.response?.data;
      let msg = 'Failed to save';
      if (typeof data === 'string') msg = data;
      else if (data?.error) msg = data.error;
      else if (err.message) msg = err.message;
      setHoursError(msg);
    } finally {
      setHoursSaving(false);
    }
  };

  const handleInviteEA = async () => {
    if (!eaName || !eaEmail) {
      setEaError('Name and email are required');
      return;
    }
    setEaInviting(true);
    setEaError(null);
    try {
      // Generate a secure temporary password
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*';
      let pw = '';
      const arr = new Uint8Array(20);
      crypto.getRandomValues(arr);
      for (let i = 0; i < 20; i++) pw += chars[arr[i] % chars.length];

      await authApi.registerUser({
        email: eaEmail,
        password: pw,
        displayName: eaName,
        tenantId: user?.tenantId || '',
        role: 'ExecutiveAssistant',
      });
      setEaInvited(true);
      setTimeout(() => setActiveStep(4), 500);
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      setEaError(errors ? (Array.isArray(errors) ? errors.join(', ') : String(errors)) : 'Failed to invite EA');
    } finally {
      setEaInviting(false);
    }
  };

  // Format minute of day to HH:MM display
  const formatMinuteOfDay = (m: number) => {
    const h = Math.floor(m / 60);
    const min = m % 60;
    return `${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
  };

  const steps: {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    content: React.ReactNode;
  }[] = [
    {
      label: 'Welcome',
      icon: PartyPopper,
      content: (
        <div>
          <p className="mb-2 text-xl font-semibold">Welcome to Cadence, {displayName}!</p>
          <p className="mb-2 text-sm text-muted-foreground">
            Let's get you set up in 3 minutes. Cadence will help you govern your time, focus, and
            strategic execution through structured daily operating rhythms.
          </p>
          <p className="mb-4 text-xs italic text-muted-foreground">
            You can customise your branding (logo, colours, product name) later in Organisation settings.
          </p>
          <Button onClick={() => setActiveStep(1)}>Let's Get Started</Button>
        </div>
      ),
    },
    {
      label: 'Connect Your Calendar',
      icon: CalendarRange,
      content: (
        <div>
          <p className="mb-4 text-sm text-muted-foreground">
            Connect your work calendar so Cadence can plan your day around your meetings. Private
            calendar events will be treated as priority meetings.
          </p>
          {calendarError && (
            <Alert variant="error" className="mb-3">
              <AlertDescription>{calendarError}</AlertDescription>
            </Alert>
          )}
          {calendarConnected ? (
            <Alert variant="success" className="mb-3">
              <AlertDescription className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Calendar connected successfully!
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="mb-3 flex flex-col gap-2">
                <Button
                  onClick={() => handleConnectCalendar('microsoft')}
                  disabled={connecting !== null}
                  className="h-11 w-full justify-start gap-3 bg-[#0078D4] pl-4 text-white normal-case hover:bg-[#106EBE]"
                >
                  <MicrosoftIcon />
                  {connecting === 'microsoft' ? 'Connecting...' : 'Connect Microsoft 365'}
                </Button>
                <Button
                  onClick={() => handleConnectCalendar('google')}
                  disabled={connecting !== null}
                  className="h-11 w-full justify-start gap-3 border border-white/20 bg-[#1A1A1A] pl-4 text-white normal-case hover:bg-[#2D2D2D]"
                >
                  <GoogleIcon />
                  {connecting === 'google' ? 'Connecting...' : 'Connect Google Calendar'}
                </Button>
              </div>
              <label className="mb-3 flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                <Checkbox
                  checked={isPersonal}
                  onCheckedChange={(c) => setIsPersonal(!!c)}
                />
                This is a personal calendar
              </label>
              <Button
                variant="ghost"
                onClick={() => { setCalendarSkipped(true); setActiveStep(2); }}
                className="gap-1.5 text-muted-foreground"
              >
                <SkipForward className="h-4 w-4" />
                Skip — I'll add an ICS feed later
              </Button>
            </>
          )}
        </div>
      ),
    },
    {
      label: 'Set Your Working Hours',
      icon: Clock,
      content: (
        <div>
          <p className="mb-4 text-sm text-muted-foreground">
            These help Cadence schedule your work in the right time slots and protect your lunch break.
          </p>
          {hoursError && (
            <Alert variant="error" className="mb-3">
              <AlertDescription>{hoursError}</AlertDescription>
            </Alert>
          )}
          {hoursSaved && (
            <Alert variant="success" className="mb-3">
              <AlertDescription className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Working hours saved!
              </AlertDescription>
            </Alert>
          )}
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="day-start">Day Start</Label>
                <Input
                  id="day-start"
                  type="time"
                  value={workDayStart}
                  onChange={(e) => setWorkDayStart(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="day-end">Day End</Label>
                <Input
                  id="day-end"
                  type="time"
                  value={workDayEnd}
                  onChange={(e) => setWorkDayEnd(e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="lunch-start">Lunch Start</Label>
                <Input
                  id="lunch-start"
                  type="time"
                  value={formatMinuteOfDay(lunchStartMinute)}
                  onChange={(e) => {
                    const [h, m] = e.target.value.split(':').map(Number);
                    if (!isNaN(h) && !isNaN(m)) setLunchStartMinute(h * 60 + m);
                  }}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="lunch-duration">Lunch Duration (min)</Label>
                <Input
                  id="lunch-duration"
                  type="number"
                  min={0}
                  max={120}
                  value={lunchDurationMinutes}
                  onChange={(e) => setLunchDurationMinutes(parseInt(e.target.value) || 0)}
                />
              </div>
            </div>
          </div>
          <Button
            onClick={handleSaveHours}
            disabled={hoursSaving || hoursSaved}
            className="mt-4"
          >
            {hoursSaving ? 'Saving...' : hoursSaved ? 'Saved' : 'Save Working Hours'}
          </Button>
        </div>
      ),
    },
    {
      label: 'Invite Your EA',
      icon: UserPlus,
      content: (
        <div>
          <p className="mb-4 text-sm text-muted-foreground">
            Do you have an Executive Assistant? Invite them to help manage your day. They will be
            able to create work items, manage your plan, and upload images on your behalf.
          </p>
          {eaError && (
            <Alert variant="error" className="mb-3">
              <AlertDescription>{eaError}</AlertDescription>
            </Alert>
          )}
          {eaInvited && (
            <Alert variant="success" className="mb-3">
              <AlertDescription className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Invitation sent to {eaEmail}! They will receive a welcome email with login instructions.
              </AlertDescription>
            </Alert>
          )}
          {!eaInvited && (
            <>
              <div className="mb-3 flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="ea-name">EA Name</Label>
                  <Input
                    id="ea-name"
                    placeholder="e.g. Sarah Johnson"
                    value={eaName}
                    onChange={(e) => setEaName(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="ea-email">EA Email</Label>
                  <Input
                    id="ea-email"
                    type="email"
                    placeholder="ea@company.com"
                    value={eaEmail}
                    onChange={(e) => setEaEmail(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleInviteEA}
                  disabled={eaInviting}
                  className="gap-1.5"
                >
                  <UserPlus className="h-4 w-4" />
                  {eaInviting ? 'Inviting...' : 'Invite EA'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { setEaSkipped(true); setActiveStep(4); }}
                  className="gap-1.5 text-muted-foreground"
                >
                  <SkipForward className="h-4 w-4" />
                  Skip — I'll do this later
                </Button>
              </div>
            </>
          )}
        </div>
      ),
    },
    {
      label: "You're ready",
      icon: CheckCircle2,
      content: (
        <div>
          <p className="mb-2 text-xl font-semibold">Your Cadence is set up.</p>
          <p className="mb-4 text-sm text-muted-foreground">
            Click "Go to Dashboard" to generate your first daily plan. Cadence will sync your
            calendar, apply your preferences, and build a structured schedule for your day.
          </p>
          <Card className="mb-4 bg-card/60">
            <CardContent className="py-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Setup Checklist
              </p>
              <div className="flex flex-col gap-2">
                <ChecklistItem
                  completed={calendarConnected}
                  skipped={calendarSkipped}
                  label="Calendar connected"
                />
                <ChecklistItem completed={hoursSaved} label="Working hours set" />
                <ChecklistItem
                  completed={eaInvited}
                  skipped={eaSkipped}
                  label="EA invited"
                />
              </div>
            </CardContent>
          </Card>
          <Button onClick={() => navigate('/')} size="lg">
            Go to Dashboard
          </Button>
        </div>
      ),
    },
  ];

  const totalSteps = steps.length;
  const progressPct = Math.round((activeStep / (totalSteps - 1)) * 100);

  return (
    <div className="mx-auto max-w-[700px] p-4 md:p-6">
      <div className="mb-1 flex items-start justify-between gap-2">
        <div>
          <h1 className="mb-1 text-2xl font-semibold tracking-tight">Getting Started</h1>
          <p className="text-sm text-muted-foreground">Set up your Cadence in a few quick steps</p>
        </div>
        <span className="inline-flex items-center rounded-full border border-border bg-transparent px-3 py-0.5 text-xs font-semibold">
          Step {activeStep + 1} of {totalSteps}
        </span>
      </div>

      {/* Progress bar */}
      <div className="mb-6 mt-3">
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <p className="mt-1 text-right text-xs text-muted-foreground">{progressPct}% complete</p>
      </div>

      {/* Vertical stepper */}
      <ol className="flex flex-col">
        {steps.map((step, index) => {
          const isComplete = index < activeStep;
          const isCurrent = index === activeStep;
          const StepIcon = step.icon;
          const isLast = index === steps.length - 1;
          return (
            <li key={step.label} className="flex gap-4">
              {/* Numbered circle + connector line */}
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    'grid h-9 w-9 shrink-0 place-items-center rounded-full border-2 transition-colors',
                    isComplete && 'border-emerald-500 bg-emerald-500 text-white',
                    isCurrent && 'border-primary bg-primary text-primary-foreground',
                    !isComplete && !isCurrent && 'border-border bg-card text-muted-foreground',
                  )}
                >
                  {isComplete
                    ? <CheckCircle2 className="h-4 w-4" />
                    : <StepIcon className="h-4 w-4" />}
                </div>
                {!isLast && (
                  <div
                    className={cn(
                      'min-h-[24px] w-px flex-1',
                      isComplete ? 'bg-emerald-500' : 'bg-border',
                    )}
                  />
                )}
              </div>

              {/* Step label + content */}
              <div className={cn('flex-1 pb-6', isLast && 'pb-0')}>
                <button
                  type="button"
                  onClick={() => { if (isComplete) setActiveStep(index); }}
                  disabled={!isComplete}
                  className={cn(
                    'text-left',
                    isComplete && 'cursor-pointer hover:underline',
                  )}
                >
                  <p
                    className={cn(
                      'text-base',
                      isCurrent ? 'font-semibold text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {step.label}
                  </p>
                  {isComplete && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400">Complete</p>
                  )}
                </button>
                {isCurrent && <div className="mt-3">{step.content}</div>}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Skip-all */}
      {activeStep < totalSteps - 1 && (
        <div className="mt-3 text-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-muted-foreground normal-case"
          >
            I'll set up later — skip to dashboard
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChecklistItem — single line in the final-step setup checklist. Emerald
// check when completed, muted skip arrow when skipped, empty circle otherwise.
// ---------------------------------------------------------------------------
function ChecklistItem({
  completed,
  skipped,
  label,
}: {
  completed: boolean;
  skipped?: boolean;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2">
      {completed ? (
        <CheckCircle2 className="h-5 w-5 text-emerald-500" />
      ) : skipped ? (
        <SkipForward className="h-5 w-5 text-muted-foreground" />
      ) : (
        <Circle className="h-5 w-5 text-muted-foreground" />
      )}
      <p
        className={cn(
          'text-sm',
          completed && 'text-emerald-600 dark:text-emerald-400',
          skipped && 'text-muted-foreground line-through',
          !completed && !skipped && 'text-foreground',
        )}
      >
        {label}{skipped ? ' (skipped)' : ''}
      </p>
    </div>
  );
}
